import React, { useEffect, useState, useCallback } from 'react';
import { Box, Stack, Button, Grid, Typography, Paper, Card, CardContent, FormControl, InputLabel, Select, MenuItem, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Chip, IconButton, DialogTitle, DialogContent } from "@mui/material";
import AppDialog from "../../../common/AppDialog";
import { Groups, HowToReg, PersonOff, AlarmOn, EventNote, LocationOn, LocationOff, AccessTime, ArrowBack, Close as CloseIcon, RestartAlt } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { getData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";
import Loader from '../../../common/spinner';
import MyAttendance from '../../../components/common/myAttendance';

const StatCard = ({ title, value, icon, color }) => (
    <Card
        sx={{
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            height: '100%',
            background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
            color: '#FFFFFF',
            position: 'relative',
            overflow: 'hidden',
            border: 'none'
        }}
    >
        <CardContent sx={{ position: 'relative', zIndex: 1, p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Box>
                    <Typography
                        variant="caption"
                        sx={{
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            fontWeight: '600',
                            opacity: 0.9,
                            display: 'block'
                        }}
                    >
                        {title}
                    </Typography>
                    <Typography variant="h4" fontWeight="800" sx={{ mt: 0.5 }}>
                        {value}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        p: 1.5,
                        borderRadius: '12px',
                        display: 'flex',
                        backdropFilter: 'blur(4px)'
                    }}
                >
                    {React.cloneElement(icon, { sx: { color: '#FFFFFF', fontSize: 28 } })}
                </Box>
            </Stack>
        </CardContent>
        {/* Decorative Circle */}
        <Box
            sx={{
                position: 'absolute',
                top: '-20%',
                right: '-10%',
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.1)',
                zIndex: 0
            }}
        />
    </Card>
);

function AttendanceDashboard() {
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const loggedInManagerId = userData?.id;
    const userType = userData?.user_type;
    const [viewingManager, setViewingManager] = useState(null);
    const [attendanceList, setAttendanceList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [todayAttendanceOverview, setTodayAttendanceOverview] = useState({
        totalEmployees: 0, presentToday: 0, absentToday: 0, lateArrivalToday: 0, leaveCount: 0
    });
    const [pagination, setPagination] = useState({ page: 0, limit: 10, totalCount: 0 });
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [status, setStatus] = useState('All');
    const [checkIn, setCheckIn] = useState('All');
    const [checkOut, setCheckOut] = useState('All');
    const [openMyAttendance, setOpenMyAttendance] = useState(false);
    const handleOpenMyAttendance = () => setOpenMyAttendance(true);
    const handleCloseMyAttendance = () => setOpenMyAttendance(false);
    const updatePagination = (newState) => setPagination(prev => ({ ...prev, ...newState }));

    const fetchAttendanceData = useCallback(async () => {
        const activeManagerId = viewingManager ? viewingManager.user_id : loggedInManagerId;
        if (!activeManagerId) return;

        try {
            setLoading(true);
            const formattedDate = selectedDate.format('MM/DD/YYYY');
            let url = `${API_ENDPOINTS.fetchAttendanceUnderManager}?Date=${formattedDate}&manager_id=${activeManagerId}&page=${pagination.page + 1}&limit=${pagination.limit}`;

            if (status !== 'All') url += `&status=${status}`;
            if (checkIn !== 'All') url += `&in_office_check_in=${checkIn.toLowerCase()}`;
            if (checkOut !== 'All') url += `&in_office_check_out=${checkOut.toLowerCase()}`;

            const response = await getData(url);
            const result = response.data || response;

            if (result.status === 'SUCCESS') {
                const messageBody = result.message;
                setAttendanceList(messageBody.data || []);
                updatePagination({ totalCount: messageBody.total || 0 });

                if (messageBody.summary) {
                    const s = messageBody.summary;
                    setTodayAttendanceOverview({
                        totalEmployees: s.total_employees || 0,
                        presentToday: s.present || 0,
                        absentToday: s.absent || 0,
                        lateArrivalToday: s.late || 0,
                        leaveCount: s.leave || 0
                    });
                }
            }
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    }, [loggedInManagerId, viewingManager, selectedDate, pagination.page, pagination.limit, status, checkIn, checkOut]);

    useEffect(() => {
        fetchAttendanceData();
    }, [fetchAttendanceData]);

    const handleViewTeam = (row) => {
        setViewingManager(row);
        updatePagination({ page: 0 });
    };

    const handleBack = () => {
        setViewingManager(null);
        updatePagination({ page: 0 });
    };

    const handleFilterChange = (setter) => (e) => {
        setter(e.target.value);
        updatePagination({ page: 0 });
    };

    const getStatusChip = (status) => {
        const config = {
            'Present': { color: 'success', label: 'Present' },
            'Absent': { color: 'error', label: 'Absent' },
            'Late': { color: 'warning', label: 'Late' },
            'Leave': { color: 'info', label: 'Leave' },
            null: { color: 'default', label: 'Not Marked' }
        };
        const active = config[status] || config[null];
        return <Chip label={active.label} color={active.color} size="small" variant="outlined" />;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Loader />
            </Box>
        );
    }

    const handleReset = () => {
        setSelectedDate(dayjs());
        setStatus('All');
        setCheckIn('All');
        setCheckOut('All');
        updatePagination({ page: 0 });
    };

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    {viewingManager && (
                        <IconButton onClick={handleBack} sx={{ bgcolor: '#f1f5f9' }}><ArrowBack /></IconButton>
                    )}
                    <Box>
                        <Typography variant="h5" fontWeight="700" color="#0f172a">
                            {viewingManager ? `Team: ${viewingManager.name}` : "Attendance Overview"}
                        </Typography>
                        <Typography variant="body2" color="#64748b">
                            {viewingManager ? `Viewing employees under ${viewingManager.name}` : "Real-time status of your team's attendance"}
                        </Typography>
                    </Box>
                </Stack>
                {!viewingManager && (
                    <Button variant="contained" disableElevation sx={{ borderRadius: '10px', textTransform: 'none', bgcolor: '#0f172a' }} onClick={handleOpenMyAttendance}>
                        My Attendance
                    </Button>
                )}
            </Stack>

            <Grid container spacing={3} sx={{ mb: 4 }}>
                {[
                    { title: 'Total Strength', value: todayAttendanceOverview.totalEmployees, icon: <Groups />, color: '#6366F1' },
                    { title: 'Present', value: todayAttendanceOverview.presentToday, icon: <HowToReg />, color: '#10B981' },
                    { title: 'Absent', value: todayAttendanceOverview.absentToday, icon: <PersonOff />, color: '#EF4444' },
                    { title: 'Late Arrival', value: todayAttendanceOverview.lateArrivalToday, icon: <AlarmOn />, color: '#F59E0B' },
                    { title: 'On Leave', value: todayAttendanceOverview.leaveCount, icon: <EventNote />, color: '#A18CD1' }
                ].map((card, idx) => (
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }} key={idx}>
                        <StatCard
                            title={card.title}
                            value={card.value || 0}
                            icon={card.icon}
                            color={card.color}
                        />
                    </Grid>
                ))}
            </Grid>

            {/* Filters */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container spacing={2} alignItems="center" mb={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                        <DatePicker label="Select Date" value={selectedDate} onChange={(val) => setSelectedDate(val)} slotProps={{ textField: { fullWidth: true, size: 'small' } }} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select value={status} label="Status" onChange={handleFilterChange(setStatus)}>
                                <MenuItem value="All">All</MenuItem>
                                <MenuItem value="Present">Present</MenuItem>
                                <MenuItem value="Absent">Absent</MenuItem>
                                <MenuItem value="Late">Late Arrival</MenuItem>
                                <MenuItem value="Leave">Leave</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Office Check-In</InputLabel>
                            <Select value={checkIn} label="Office Check-In" onChange={handleFilterChange(setCheckIn)}>
                                <MenuItem value="All">All</MenuItem>
                                <MenuItem value="Yes">Yes</MenuItem>
                                <MenuItem value="No">No</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Office Check-Out</InputLabel>
                            <Select value={checkOut} label="Office Check-Out" onChange={handleFilterChange(setCheckOut)}>
                                <MenuItem value="All">All</MenuItem>
                                <MenuItem value="Yes">Yes</MenuItem>
                                <MenuItem value="No">No</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <Button
                            fullWidth
                            onClick={handleReset}
                            startIcon={<RestartAlt />}
                            sx={{ textTransform: 'none', color: 'text.secondary' }}
                        >
                            Reset Filters
                        </Button>
                    </Grid>
                </Grid>
            </LocalizationProvider>

            <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', overflow: 'hidden', border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 900 }}>
                        <TableHead sx={{ bgcolor: '#EFF6FF' }}>
                            <TableRow>
                                {['Employee Name', 'Status', 'Check-in', 'Check-out', 'In Office In', 'In Office Out', 'Production', 'Actions'].map(h => (
                                    <TableCell key={h} sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ bgcolor: '#FFFFFF' }}>
                            {attendanceList.length > 0 ? attendanceList.map((row) => (
                                <TableRow key={row.user_id} hover sx={{
                                    '&:hover': {
                                        bgcolor: '#F1F5F9 !important'
                                    }
                                }}>
                                    <TableCell sx={{ color: '#1E293B', fontWeight: 600 }}>{row.name}</TableCell>
                                    <TableCell sx={{ color: '#1E293B' }}>{getStatusChip(row.status)}</TableCell>
                                    <TableCell sx={{ color: '#1E293B' }}>
                                        {row.check_in
                                            ? new Date(row.check_in).toLocaleTimeString('en-US', {
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: true
                                            })
                                            : '--:--'
                                        }
                                    </TableCell>
                                    <TableCell sx={{ color: '#1E293B' }}>
                                        {row.check_out ? dayjs(row.check_out).format('hh:mm A') : '--:--'}
                                    </TableCell>
                                    {/* In Office In Column */}
                                    <TableCell sx={{ color: '#1E293B' }}>
                                        <Stack direction="row" alignItems="center" gap={1}>
                                            {row.office_premises?.check_in?.in_office === "yes" ? (
                                                <>
                                                    <LocationOn sx={{ color: '#2e7d32', fontSize: 18 }} />
                                                    <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>Yes</Typography>
                                                </>
                                            ) : (
                                                <>
                                                    <LocationOff sx={{ color: '#d32f2f', fontSize: 18 }} />
                                                    <Typography variant="body2" sx={{ color: '#d32f2f' }}>No</Typography>
                                                </>
                                            )}
                                        </Stack>
                                    </TableCell>

                                    {/* In Office Out Column */}
                                    <TableCell sx={{ color: '#1E293B' }}>
                                        <Stack direction="row" alignItems="center" gap={1}>
                                            {row.office_premises?.check_out?.in_office === "yes" ? (
                                                <>
                                                    <LocationOn sx={{ color: '#2e7d32', fontSize: 18 }} />
                                                    <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>Yes</Typography>
                                                </>
                                            ) : (
                                                <>
                                                    <LocationOff sx={{ color: '#d32f2f', fontSize: 18 }} />
                                                    <Typography variant="body2" sx={{ color: '#d32f2f' }}>No</Typography>
                                                </>
                                            )}
                                        </Stack>
                                    </TableCell>
                                    <TableCell sx={{ color: '#1E293B' }}>
                                        <Stack direction="row" alignItems="center" gap={1}>
                                            <AccessTime sx={{ color: '#64748b', fontSize: 18 }} />
                                            {row.total_hours ? (() => {
                                                const [hours, minutes] = row.total_hours.split(':');
                                                return `${parseInt(hours)}h ${parseInt(minutes)}m`;
                                            })() : '0h 0m'}
                                        </Stack>
                                    </TableCell>
                                    <TableCell sx={{ color: '#1E293B' }}>
                                        {['Manager', 'Team Lead'].includes(userType) &&
                                            ['Manager', 'Team Lead'].includes(row.user_type) &&
                                            !viewingManager && (
                                                <Button variant="outlined" size="small" sx={{ textTransform: 'none' }} onClick={() => handleViewTeam(row)}>
                                                    View Team
                                                </Button>
                                            )}
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        {!loading && "No records found."}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </TableContainer>

            <TablePagination
                component="div"
                count={pagination.totalCount}
                page={pagination.page}
                onPageChange={(_, newPage) => updatePagination({ page: newPage })}
                rowsPerPage={pagination.limit}
                onRowsPerPageChange={(e) => updatePagination({ limit: parseInt(e.target.value, 10), page: 0 })}
                rowsPerPageOptions={[5, 10, 25]}
            />
            <AppDialog
                open={openMyAttendance}
                onClose={handleCloseMyAttendance}
                fullWidth
                maxWidth="lg" // Adjust size as needed
                PaperProps={{
                    sx: { borderRadius: '16px' }
                }}
            >
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" component="span" fontWeight="700">
                        My Attendance
                    </Typography>
                    <IconButton onClick={handleCloseMyAttendance}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 2 }}>
                    <MyAttendance />
                </DialogContent>
            </AppDialog>
        </Box>
    );
}

export default AttendanceDashboard;